import { useMemo } from 'react';
import { useApp } from '@/context/AppContext';
import { StatusBadge } from '@/components/ui/Badge';

function timeAgo(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diffMs = Date.now() - then;
  const min = Math.round(diffMs / 60_000);
  if (min < 1)   return 'just now';
  if (min < 60)  return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24)   return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7)   return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

function initialsFor(name) {
  return (name || '').split(/\s+/).filter(Boolean).map(p => p[0]).slice(0, 2).join('').toUpperCase() || '??';
}

function StageDot({ stageId }) {
  const stageColors = ['#7c3aed','#0891b2','#d97706','#f97316','#059669','#be185d','#dc2626','#1e3a5f'];
  const color = stageColors[(stageId || 1) - 1] || '#94a3b8';
  return <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />;
}

const STAGE_NAMES = ['Detail','Material Cut','Fit-Up','Weld','Grind/Clean','Paint','Final QC','Ship'];

export default function Dashboard() {
  const {
    stats, jobs, pieceMarks, team, customers, invoices, bids, timeCards,
    setScreen, openModal, userEmail, custById, stageById, empById, fabStages,
  } = useApp();

  const greetingName = useMemo(() => {
    if (!userEmail) return 'there';
    const local = userEmail.split('@')[0];
    return local.charAt(0).toUpperCase() + local.slice(1);
  }, [userEmail]);

  // Active jobs with piece-mark progress
  const activeJobRows = useMemo(() => {
    return jobs
      .filter(j => j.status === 'active')
      .slice(0, 6)
      .map(j => {
        const marks = pieceMarks.filter(p => p.jobId === j.id);
        const shipped = marks.filter(p => p.currentStageId === 8 || p.status === 'complete').length;
        const pct = marks.length ? Math.round((shipped / marks.length) * 100) : 0;
        const cust = custById(j.customerId);
        return { job: j, marks: marks.length, shipped, pct, cust };
      });
  }, [jobs, pieceMarks, custById]);

  // Stage heatmap — count pieces at each stage across all active jobs
  const stageHeatmap = useMemo(() => {
    const activePMs = pieceMarks.filter(pm => {
      const job = jobs.find(j => j.id === pm.jobId);
      return job?.status === 'active';
    });
    return fabStages.map(stage => ({
      stage,
      count: activePMs.filter(pm => pm.currentStageId === stage.id).length,
    }));
  }, [pieceMarks, jobs, fabStages]);

  const maxHeatCount = Math.max(1, ...stageHeatmap.map(s => s.count));

  // Activity feed (amounts are stored in cents).
  const activity = useMemo(() => {
    const items = [];
    invoices.forEach(i => {
      if (i.status === 'paid' && i.paidDate) {
        const c = custById(i.customerId);
        const dollars = Math.round((Number(i.amount) || 0) / 100);
        items.push({ when: i.paidDate, dot: 'var(--teal)', title: `Invoice paid — $${dollars.toLocaleString()}`, sub: c?.name || i.service || 'Customer' });
      }
    });
    bids.forEach(b => {
      if (b.status === 'approved' && b.createdAt) {
        const c = custById(b.customerId);
        items.push({ when: b.createdAt, dot: 'var(--violet)', title: `Bid approved`, sub: `${c?.name || 'GC'} — ${b.projectName}` });
      }
    });
    jobs.forEach(j => {
      if (j.createdAt) {
        const c = custById(j.customerId);
        items.push({ when: j.createdAt, dot: 'var(--amber)', title: 'Job created', sub: `${j.jobNumber} — ${c?.name || ''}` });
      }
    });
    items.sort((a, b) => (b.when || '').localeCompare(a.when || ''));
    return items.slice(0, 6);
  }, [invoices, bids, jobs, custById]);

  const activeTeam = useMemo(() => team.filter(t => t.active !== false), [team]);

  // Clock-in status: who's currently clocked in?
  const clockedIn = useMemo(() => {
    const now = new Date();
    const today = now.toISOString().slice(0, 10);
    const emp = {};
    timeCards
      .filter(tc => tc.punchedAt?.slice(0, 10) === today)
      .sort((a, b) => (a.punchedAt || '').localeCompare(b.punchedAt || ''))
      .forEach(tc => { emp[tc.employeeId] = tc.punchType; });
    return emp; // employeeId -> 'clock_in' | 'clock_out'
  }, [timeCards]);

  return (
    <div className="page fade-up">
      {/* Header */}
      <div className="ph">
        <div>
          <div className="ptitle">Welcome back, {greetingName}</div>
          <div className="psub">
            {stats.activeJobs} active job{stats.activeJobs !== 1 ? 's' : ''} · {stats.piecesInProgress} pieces in progress · {activeTeam.length} employee{activeTeam.length !== 1 ? 's' : ''}
          </div>
        </div>
        <div className="pacts">
          <button className="btn btn-ghost btn-sm" onClick={() => setScreen('reports')}>Reports</button>
          <button className="btn btn-primary btn-sm" onClick={() => openModal('add_job')}>
            + New Job
          </button>
        </div>
      </div>

      {/* KPI row */}
      <div className="g4">
        <div className="kpi" onClick={() => setScreen('jobs')}>
          <div className="kpi-bar" style={{ background: 'linear-gradient(90deg,var(--cyan),transparent)' }} />
          <div className="kpi-icon">🏗️</div>
          <div className="kpi-label">Active Jobs</div>
          <div className="kpi-value">{stats.activeJobs}</div>
          <div className="kpi-sub">{stats.piecesInProgress} piece{stats.piecesInProgress !== 1 ? 's' : ''} in progress</div>
        </div>
        <div className="kpi" onClick={() => setScreen('time-cards')}>
          <div className="kpi-bar" style={{ background: 'linear-gradient(90deg,var(--amber),transparent)' }} />
          <div className="kpi-icon">⏱️</div>
          <div className="kpi-label">Labor Hours</div>
          <div className="kpi-value">{stats.laborHrsWeek}</div>
          <div className="kpi-sub">This week</div>
        </div>
        <div className="kpi" onClick={() => setScreen('bids')}>
          <div className="kpi-bar" style={{ background: 'linear-gradient(90deg,var(--violet),transparent)' }} />
          <div className="kpi-icon">📋</div>
          <div className="kpi-label">Pending Bids</div>
          <div className="kpi-value">{stats.pendingBids}</div>
          <div className="kpi-sub">Awaiting GC response</div>
        </div>
        <div className="kpi" onClick={() => setScreen('customers')}>
          <div className="kpi-bar" style={{ background: 'linear-gradient(90deg,var(--rose),transparent)' }} />
          <div className="kpi-icon">⚠️</div>
          <div className="kpi-label">Overdue Invoices</div>
          <div className="kpi-value">${stats.overdueAmount.toLocaleString()}</div>
          <div className="kpi-sub">
            {stats.overdueCount === 0 ? 'All accounts current' : `${stats.overdueCount} account${stats.overdueCount > 1 ? 's' : ''} past due`}
          </div>
        </div>
      </div>

      {/* Stage Heatmap */}
      <div className="card">
        <div className="card-head">
          <span className="card-title">Live Stage Heatmap — All Active Jobs</span>
          <button className="btn btn-ghost btn-sm" onClick={() => setScreen('jobs')}>View Jobs →</button>
        </div>
        <div className="card-body">
          <div style={{ display: 'flex', gap: 8 }}>
            {stageHeatmap.map(({ stage, count }) => {
              const pct = Math.round((count / maxHeatCount) * 100);
              return (
                <div key={stage.id} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: count > 0 ? 'var(--t1)' : 'var(--t4)' }}>{count}</div>
                  <div style={{ width: '100%', height: 50, background: 'var(--s3)', borderRadius: 6, overflow: 'hidden', display: 'flex', alignItems: 'flex-end' }}>
                    <div style={{ width: '100%', height: `${Math.max(pct, 4)}%`, background: stage.color || 'var(--accent)', borderRadius: 6, transition: 'height .4s ease', opacity: count > 0 ? 1 : 0.25 }} />
                  </div>
                  <div style={{ fontSize: 9.5, fontWeight: 600, color: 'var(--t3)', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                    {stage.name.replace('/', '/\u200B')}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Three-column grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 280px', gap: 12 }}>
        {/* Active jobs */}
        <div className="card">
          <div className="card-head">
            <span className="card-title">Active Jobs</span>
            <button className="btn btn-ghost btn-sm" onClick={() => setScreen('jobs')}>All Jobs →</button>
          </div>
          <div className="card-body-0">
            {activeJobRows.length === 0 ? (
              <div style={{ padding: '24px', textAlign: 'center', color: 'var(--t3)', fontSize: 12.5 }}>
                No active jobs — <button className="auth-link" onClick={() => openModal('add_job')}>create your first job</button>
              </div>
            ) : activeJobRows.map(({ job, marks, shipped, pct, cust }) => (
              <div key={job.id} className="job-row" onClick={() => setScreen('jobs')}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--t1)' }}>
                    {job.jobNumber}
                    <span style={{ fontWeight: 400, color: 'var(--t3)', marginLeft: 6 }}>{cust?.name || ''}</span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{job.projectName}</div>
                  <div style={{ marginTop: 5 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div className="prog-track" style={{ height: 4, flex: 1 }}>
                        <div className="prog-fill" style={{ width: `${pct}%`, background: pct === 100 ? 'var(--teal)' : 'var(--accent)' }} />
                      </div>
                      <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--t3)', width: 32, textAlign: 'right' }}>{pct}%</span>
                    </div>
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 11, color: 'var(--t3)' }}>{shipped}/{marks} shipped</div>
                  {job.dueDate && <div style={{ fontSize: 10.5, color: 'var(--t4)', marginTop: 2 }}>Due {job.dueDate}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Team clock-in status */}
        <div className="card">
          <div className="card-head">
            <span className="card-title">Team Today</span>
            <button className="btn btn-ghost btn-sm" onClick={() => setScreen('time-cards')}>Time Cards →</button>
          </div>
          <div className="card-body">
            {activeTeam.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ fontSize: 12, color: 'var(--t3)', marginBottom: 10 }}>No employees yet</div>
                <button className="btn btn-primary btn-sm" onClick={() => setScreen('team')}>+ Add Employee</button>
              </div>
            ) : activeTeam.map(emp => {
              const status = clockedIn[emp.id];
              const isClockedIn = status === 'clock_in';
              const initials = emp.initials || initialsFor(emp.name);
              return (
                <div key={emp.id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <div className="emp-av" style={{ background: emp.color || 'var(--cyan)' }}>{initials}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--t1)' }}>{emp.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--t3)' }}>{emp.role}</div>
                  </div>
                  <span
                    className="bdg"
                    style={{
                      background: isClockedIn ? 'var(--teal-t)' : 'var(--s3)',
                      color:      isClockedIn ? 'var(--teal)'   : 'var(--t4)',
                    }}
                  >
                    {isClockedIn ? '● In' : '○ Out'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Activity feed */}
        <div className="card">
          <div className="card-head"><span className="card-title">Activity</span></div>
          <div className="card-body-0">
            {activity.length === 0 ? (
              <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--t3)', fontSize: 12 }}>
                Paid invoices, approved bids, and new jobs will appear here.
              </div>
            ) : activity.map((a, i) => (
              <div key={i} className="tl-item">
                <div className="tl-dot" style={{ background: a.dot }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="tl-title">{a.title}</div>
                  <div className="tl-sub">{a.sub}</div>
                </div>
                <div className="tl-time">{timeAgo(a.when)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
